import {
  Button,
  Select,
  SelectOptionType,
  TextOutlinedInput,
} from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../locales/translations';
import { useRiskEvaluationsDataLoader } from '../loaders/StudentRiskEvaluationSessionsDataLoader';
import { useWarningsSummaryFormLoaderData } from '../loaders/StudentWarningsSummaryFormLoader';

const StudentRiskEvaluationSessionsFilters = () => {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_RISK_EVALUATION_SESSIONS_LIST,
  );
  const data = useWarningsSummaryFormLoaderData();
  const { loading, query, updateQuery } = useRiskEvaluationsDataLoader();

  const [startAt, setStartAt] = useState<string>();
  const [endAt, setEndAt] = useState<string>();

  const handleClear = useCallback(() => {
    setStartAt(undefined);
    setEndAt(undefined);
  }, []);

  const handleChangeFilter = useCallback(
    (key: keyof typeof query, option: SelectOptionType) => {
      let nextQuery = { ...query, [key]: option.value as string, page: 1 };
      updateQuery(nextQuery);
      handleClear();
    },
    [updateQuery, query, handleClear],
  );

  const handleSearch = useCallback(() => {
    let nextQuery = {
      ...query,
      page: 1,
      startAt,
      endAt,
    };
    updateQuery(nextQuery);
  }, [query, startAt, endAt, updateQuery]);

  useEffect(() => {
    if (query && !query?.schoolId && data?.schools?.length === 1) {
      updateQuery({
        ...query,
        schoolId: data?.schools[0].value as string,
        page: 1,
        startAt: undefined,
        endAt: undefined,
      });
    }
  }, [query, data.schools, updateQuery]);

  return (
    <Container fluid>
      <Row className="pb-4 gutters-filters">
        <Col xs={12}>
          <p>{t('filters.prefix')}</p>
        </Col>
        <Col xs={12} md={4}>
          <Select
            value={data?.schools?.find((e) => e.value === query.schoolId)}
            name="school"
            options={data.schools}
            label={t('filters.school.label')}
            placeholder={t('filters.school.placeholder')}
            onChange={handleChangeFilter.bind(null, 'schoolId')}
            isClearable={false}
            disabled={loading || data.schools.length < 1}
          />
        </Col>
      </Row>
      <Row className="pb-4 gutters-filters">
        <Col xs={12} md={3}>
          <TextOutlinedInput
            value={startAt}
            type={'date' as never}
            name="startAt"
            label={t('filters.startAt.label')}
            placeholder={t('filters.startAt.placeholder')}
            onChange={(e) => setStartAt(e.target.value)}
            disabled={loading || !query?.schoolId}
          />
        </Col>
        <Col xs={12} md={3}>
          <TextOutlinedInput
            value={endAt}
            type={'date' as never}
            name="endAt"
            label={t('filters.endAt.label')}
            placeholder={t('filters.endAt.placeholder')}
            onChange={(e) => setEndAt(e.target.value)}
            disabled={loading || !query?.schoolId}
          />
        </Col>
        <Col xs={12} md={6} className="mt-4">
          <div className="d-flex justify-content-end">
            <Button
              text={t('common:actions.search')}
              size="md"
              type="button"
              disabled={!query?.schoolId}
              onClick={handleSearch}
              loading={loading}
            />
            <Button
              text={t('common:actions.clean')}
              className="ml-2"
              size="md"
              outlined
              type="button"
              disabled={!query?.schoolId || loading}
              onClick={handleClear}
            />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default StudentRiskEvaluationSessionsFilters;
